import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, CATEGORIES as FALLBACK, BACKEND_URL } from "@/lib/api";

const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  return `${BACKEND_URL}${src}`;
};

export default function FeaturedCategories() {
  const [categories, setCategories] = useState(FALLBACK);

  useEffect(() => {
    api
      .get("/category-meta")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setCategories(r.data);
      })
      .catch(() => {
        /* keep static fallback */
      });
  }, []);

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="featured-categories-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-end justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="divider-gold" />
              <span className="overline">The Collection</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light max-w-2xl leading-[1.02]">
              A ritual for every <em className="gold-text not-italic">facet</em> of you.
            </h2>
          </div>
          <Link to="/shop" className="overline hover:text-[#C9A961] transition-colors flex items-center gap-2" data-testid="categories-view-all">
            View All Categories <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {categories.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
            >
              <Link
                to={`/shop?cat=${encodeURIComponent(c.name)}`}
                className="group block relative aspect-[3/4] overflow-hidden bg-[#F8F5F0]"
                data-testid={`category-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <img
                  src={resolveImg(c.image)}
                  alt={`${c.name} — ${c.tagline || "Blisson Professional collection"}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 category-card-overlay" />
                <div className="absolute inset-0 p-7 md:p-9 flex flex-col justify-end text-white">
                  <div className="overline text-white/75 mb-2">{c.tagline}</div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif-display text-3xl md:text-4xl font-light">{c.name}</h3>
                    <div className="w-12 h-12 rounded-full border border-white/40 flex items-center justify-center transition-all duration-500 group-hover:bg-[#C9A961] group-hover:border-[#C9A961]">
                      <ArrowUpRight size={16} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
